/**
 * Standard ERP payment line shape for all document types (sales, purchase,
 * old metal, custom order, repairing order).
 *
 * Sale create (cash/bank):
 *   { bank_account_id, amount, payment_mode, bank_name, idempotency_key, remarks }
 *
 * Sale create (Old Gold/Silver):
 *   { payment_mode: "OLD_GOLD"|"OLD_SILVER", amount, idempotency_key, remarks }
 *   urd_document_id is sent only when linking an existing URD document.
 *   — no bank_account_id
 *
 * Sale create (Kasar):
 *   { payment_mode: "Kasar", amount, idempotency_key, remarks }
 *   — no bank_account_id
 *
 * Update:
 *   { id, ...fields above }
 */

import {
  hasExistingPaymentLineById,
  readPaymentIdempotencyKeyFromApi,
  resolvePaymentLineIdempotencyKey,
} from "./paymentIdempotency";

export const PAYMENT_METHOD_LABELS = {
  cash: "Cash",
  cheque: "Cheque",
  bank_transfer: "Bank Transfer",
  upi: "UPI",
  card: "Card",
  old_gold: "Old Gold",
  old_silver: "Old Silver",
  kasar: "Kasar",
  scheme: "Scheme",
  order: "Order",
  os: "OS",
  wallet: "Wallet",
};

/** API payment_mode for URD-linked sale payments. */
export const URD_PAYMENT_MODES = {
  old_gold: "OLD_GOLD",
  old_silver: "OLD_SILVER",
};

const num = (v, fallback = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
};

export function isUrdLinkedPayMode(payMode) {
  const key = String(payMode ?? "")
    .trim()
    .toLowerCase();
  return key === "old_gold" || key === "old_silver";
}

export function isUrdLinkedPaymentMode(paymentMode) {
  const raw = String(paymentMode ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
  return raw === "OLD_GOLD" || raw === "OLD_SILVER";
}

/** Kasar has no bank account — amount + payment_mode only. */
export function isKasarPayMode(payMode) {
  return (
    String(payMode ?? "")
      .trim()
      .toLowerCase() === "kasar"
  );
}

export function isKasarPaymentMode(paymentMode) {
  const raw = String(paymentMode ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
  return raw === "kasar";
}

/** Modes that must not send bank_account_id. */
export function isBanklessPayMode(payMode) {
  return isUrdLinkedPayMode(payMode) || isKasarPayMode(payMode);
}

export function isBanklessPaymentMode(paymentMode) {
  return (
    isUrdLinkedPaymentMode(paymentMode) || isKasarPaymentMode(paymentMode)
  );
}

export function urdApiModeFromPayMode(payMode) {
  const key = String(payMode ?? "")
    .trim()
    .toLowerCase();
  if (key === "old_silver") return URD_PAYMENT_MODES.old_silver;
  if (key === "old_gold") return URD_PAYMENT_MODES.old_gold;
  return null;
}

/**
 * Map API `payment_mode` label (e.g. "CASH", "OLD_GOLD") → UI `pay_mode` key.
 */
export function parsePaymentModeFromApi(paymentMode) {
  const raw = String(paymentMode ?? "").trim();
  if (!raw) return "bank_transfer";

  const upper = raw.toUpperCase().replace(/\s+/g, "_");
  if (upper === "OLD_GOLD") return "old_gold";
  if (upper === "OLD_SILVER") return "old_silver";

  const lower = raw.toLowerCase();
  const underscored = lower.replace(/\s+/g, "_");

  for (const [key, label] of Object.entries(PAYMENT_METHOD_LABELS)) {
    if (key === underscored || label.toLowerCase() === lower) {
      return key;
    }
  }

  if (lower.includes("cash")) return "cash";
  if (lower.includes("cheq") || lower.includes("check")) return "cheque";
  if (lower.includes("upi")) return "upi";
  if (lower.includes("card")) return "card";
  if (lower.includes("wallet")) return "wallet";
  if (lower.includes("old") && lower.includes("gold")) return "old_gold";
  if (lower.includes("old") && lower.includes("silver")) return "old_silver";
  if (lower.includes("kasar")) return "kasar";
  if (lower.includes("scheme")) return "scheme";
  if (lower.includes("bank") || lower.includes("transfer")) {
    return "bank_transfer";
  }

  return underscored || "bank_transfer";
}

/**
 * Resolve UI pay_mode from a full API payment line.
 * When `payment_mode` is null/empty (legacy payloads), fall back to `bank_name`
 * if it clearly names a known method (e.g. bank_name: "Cash" → cash).
 */
export function resolvePayModeFromApiPayment(payment) {
  const modeRaw = String(payment?.payment_mode ?? "").trim();
  if (modeRaw) {
    return parsePaymentModeFromApi(modeRaw);
  }

  const bankName = String(payment?.bank_name ?? "").trim();
  if (bankName) {
    const inferred = parsePaymentModeFromApi(bankName);
    if (Object.prototype.hasOwnProperty.call(PAYMENT_METHOD_LABELS, inferred)) {
      return inferred;
    }
  }

  return "bank_transfer";
}

export function parseApiPaymentAmount(payment) {
  const raw = payment?.amount ?? payment?.amt ?? payment?.total_amount;
  if (raw === "" || raw == null) return "";
  const n = parseFloat(String(raw).replace(/,/g, ""));
  return Number.isFinite(n) ? n : "";
}

function readUrdDocumentIdFromApi(payment) {
  const raw =
    payment?.urd_document_id ??
    payment?.urdDocumentId ??
    payment?.old_metal_purchase_document_id;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Resolve UI `type` (bank account id string) from API payment + bank list. */
export function resolvePaymentTypeFromApi(payment, paymentTypes = []) {
  const pay_mode = resolvePayModeFromApiPayment(payment);
  if (isUrdLinkedPayMode(pay_mode) || readUrdDocumentIdFromApi(payment)) {
    return "";
  }

  const bankAccountId = num(payment?.bank_account_id, 0);
  if (bankAccountId > 0) {
    return String(bankAccountId);
  }

  const bankName = String(payment?.bank_name ?? "").trim();
  const types = Array.isArray(paymentTypes) ? paymentTypes : [];
  if (bankName && types.length > 0) {
    const lower = bankName.toLowerCase();
    const match = types.find((b) => {
      const label = String(b.label ?? b.bankName ?? b.bank_name ?? "")
        .trim()
        .toLowerCase();
      return label === lower || label.includes(lower) || lower.includes(label);
    });
    if (match) {
      const id = match.value ?? match.id;
      if (id != null && id !== "") return String(id);
    }
  }

  return "";
}

/** Type dropdown options with bank_name fallback labels (EditInvoice / Purchase). */
export function buildPaymentTypeSelectOptionsForRow(row, paymentTypes = []) {
  if (isUrdLinkedPayMode(row?.pay_mode)) {
    return [];
  }

  let opts = (Array.isArray(paymentTypes) ? paymentTypes : []).map((pt) => ({
    value: pt.value ?? pt.id,
    label: pt.label ?? pt.bankName ?? pt.bank_name ?? String(pt.value ?? pt.id),
  }));

  const bankDisplayName = String(
    row?.bankDisplayName ?? row?.bank_name ?? "",
  ).trim();
  const typeStr =
    row?.type != null && String(row.type).trim() !== ""
      ? String(row.type).trim()
      : "";

  if (bankDisplayName && typeStr && /^\d+$/.test(typeStr)) {
    const synth = { value: typeStr, label: bankDisplayName };
    if (!opts.some((o) => String(o.value) === typeStr)) {
      opts = [synth, ...opts];
    }
  }

  if (
    bankDisplayName &&
    typeStr &&
    String(typeStr).startsWith("__acctname__:")
  ) {
    const synth = { value: typeStr, label: bankDisplayName };
    if (!opts.some((o) => String(o.value) === typeStr)) {
      opts = [synth, ...opts];
    }
  }

  return opts;
}

export function getPaymentTypeSelectedOptionForRow(row, typeOptions = []) {
  if (isUrdLinkedPayMode(row?.pay_mode)) {
    return null;
  }

  const typeStr =
    row?.type != null && String(row.type).trim() !== ""
      ? String(row.type).trim()
      : "";
  if (!typeStr) return null;

  return typeOptions.find((opt) => String(opt.value) === typeStr) || null;
}

export function formatUrdPaymentDisplayLabel(row) {
  const mode = isUrdLinkedPayMode(row?.pay_mode)
    ? PAYMENT_METHOD_LABELS[row.pay_mode] || row.pay_mode
    : "";
  const doc = readUrdDocNumber(row);
  if (mode && doc) return `${mode} (${doc})`;
  if (mode) return mode;
  return doc || "Linked URD";
}

export function readUrdDocNumber(source) {
  const raw =
    source?.urd_doc_number ??
    source?.urdDocNumber ??
    source?.doc_number ??
    source?.docNumber ??
    null;
  return raw != null && String(raw).trim() !== ""
    ? String(raw).trim()
    : null;
}

function nextPaymentRowId(rows) {
  const numericIds = (Array.isArray(rows) ? rows : [])
    .map((row) => Number(row?.id))
    .filter((id) => Number.isFinite(id));
  return numericIds.length ? Math.max(...numericIds) + 1 : 1;
}

/** Ensure a payment row exists in the list (by id, then pay_mode, else append). */
export function ensurePaymentRowInList(rows, row) {
  if (!row?.pay_mode) return Array.isArray(rows) ? rows : [];

  const list = Array.isArray(rows) ? [...rows] : [];
  const byIdIdx = list.findIndex((r) => String(r.id) === String(row.id));
  if (byIdIdx >= 0) {
    list[byIdIdx] = { ...list[byIdIdx], ...row };
    return list;
  }

  const byModeIdx = list.findIndex((r) => r.pay_mode === row.pay_mode);
  if (byModeIdx >= 0) {
    list[byModeIdx] = { ...list[byModeIdx], ...row };
    return list;
  }

  return [...list, row];
}

/** Apply URD link payload onto payment rows for sale return navigation. */
export function applyUrdLinkToPaymentRows(rows, urdPayment) {
  if (!Array.isArray(rows) || !urdPayment?.urd_document_id) return rows;

  const payMode =
    urdPayment.payment_mode === "OLD_SILVER" ? "old_silver" : "old_gold";
  const urdFields = {
    pay_mode: payMode,
    amount: String(urdPayment.amount ?? ""),
    urd_document_id: urdPayment.urd_document_id,
    urd_doc_number: urdPayment.urd_doc_number ?? null,
    type: "",
  };

  const list = [...rows];
  const byIdIdx =
    urdPayment.paymentRowId != null
      ? list.findIndex(
          (row) => String(row.id) === String(urdPayment.paymentRowId),
        )
      : -1;

  if (byIdIdx >= 0) {
    list[byIdIdx] = { ...list[byIdIdx], ...urdFields };
    return list;
  }

  const byModeIdx = list.findIndex((row) => row.pay_mode === payMode);
  if (byModeIdx >= 0) {
    list[byModeIdx] = { ...list[byModeIdx], ...urdFields };
    return list;
  }

  return [
    ...list,
    {
      id: urdPayment.paymentRowId ?? nextPaymentRowId(list),
      remarks: "",
      ...urdFields,
    },
  ];
}

/** Display label for a raw API payment line (invoice list, PDF, settlement). */
export function formatApiPaymentDisplayLabel(payment) {
  if (!payment) return "";
  const pay_mode = resolvePayModeFromApiPayment(payment);
  const urdId = readUrdDocumentIdFromApi(payment);
  if (isUrdLinkedPayMode(pay_mode) || urdId != null) {
    return formatUrdPaymentDisplayLabel({
      pay_mode,
      urd_doc_number:
        payment?.urd_doc_number ??
        payment?.urdDocNumber ??
        payment?.doc_number,
      urd_document_id: urdId,
    });
  }
  if (isKasarPayMode(pay_mode)) {
    return PAYMENT_METHOD_LABELS.kasar;
  }
  return String(payment?.bank_name || payment?.payment_mode || "").trim();
}

/**
 * Map one API/socket payment line → edit-form payment row state.
 */
export function mapApiPaymentToUiRow(payment, index = 0, options = {}) {
  const {
    includeBankRegister = false,
    formatAmountFixed2 = false,
  } = options;

  const ledgerId =
    payment?.id != null && payment.id !== "" ? Number(payment.id) : null;
  const hasLedger =
    ledgerId != null && Number.isFinite(ledgerId) && ledgerId > 0;
  const uiRowId = hasLedger ? ledgerId : index + 1;

  const pay_mode = resolvePayModeFromApiPayment(payment);
  const paymentTypes = options.paymentTypes ?? [];
  const urdDocumentId = readUrdDocumentIdFromApi(payment);
  const isUrdLinked =
    isUrdLinkedPayMode(pay_mode) || urdDocumentId != null;
  const isKasar = isKasarPayMode(pay_mode);

  let typeValue = "";
  if (!isUrdLinked && !isKasar) {
    typeValue = resolvePaymentTypeFromApi(payment, paymentTypes);
  }

  const amountRaw = parseApiPaymentAmount(payment);
  let amount = "";
  if (amountRaw !== "") {
    amount = formatAmountFixed2
      ? Number(amountRaw).toFixed(2)
      : String(amountRaw);
  }

  const transactionDate = String(payment?.transaction_date ?? "").trim();

  const row = {
    id: uiRowId,
    paymentId: hasLedger ? ledgerId : null,
    isExistingPayment: hasLedger,
    paymentIdempotencyKey: readPaymentIdempotencyKeyFromApi(payment),
    pay_mode,
    type: typeValue,
    bankDisplayName: isUrdLinked
      ? formatUrdPaymentDisplayLabel({
          pay_mode,
          urd_doc_number: payment?.urd_doc_number ?? payment?.urdDocNumber,
          urd_document_id: urdDocumentId,
        })
      : isKasar
        ? undefined
        : String(payment?.bank_name ?? "").trim() || undefined,
    remarks: payment?.remarks != null ? String(payment.remarks) : "",
    amount,
    date: transactionDate,
    created_at: transactionDate,
    urd_document_id: urdDocumentId,
    urd_doc_number:
      payment?.urd_doc_number ??
      payment?.urdDocNumber ??
      payment?.doc_number ??
      null,
    ledger_linked: payment?.ledger_linked,
  };

  if (includeBankRegister) {
    row.bank_register_id =
      payment?.bank_register_id != null && payment.bank_register_id !== ""
        ? payment.bank_register_id
        : null;
  }

  return row;
}

/** Map API `payments[]` → UI payment rows. */
export function mapApiPaymentsToUiRows(payments, options = {}) {
  if (!Array.isArray(payments) || payments.length === 0) return null;
  return payments.map((payment, index) =>
    mapApiPaymentToUiRow(payment, index, options),
  );
}

export function parsePaymentLineAmount(row) {
  const raw = row?.amount ?? row?.amt;
  if (raw === "" || raw == null) return 0;
  const n = parseFloat(String(raw).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

/** Resolve UI payment row `type` → numeric `bank_account_id` (not for URD-linked). */
export function resolvePaymentBankAccountId(row, paymentTypes = []) {
  if (isUrdLinkedPayMode(row?.pay_mode) || isKasarPayMode(row?.pay_mode)) {
    return 0;
  }

  const direct =
    row?.bank_account_id ?? row?.bank_accountId ?? row?.bank_type;
  const directNum = num(direct, NaN);
  if (Number.isFinite(directNum) && directNum > 0) {
    return directNum;
  }

  let bankTypeValue = null;
  const types = Array.isArray(paymentTypes) ? paymentTypes : [];

  if (
    typeof row?.type === "string" &&
    row.type.startsWith("__acctname__:") &&
    row.bankDisplayName &&
    types.length > 0
  ) {
    const match = types.find(
      (b) =>
        String(b.label).trim().toLowerCase() ===
        String(row.bankDisplayName).trim().toLowerCase(),
    );
    if (match) {
      bankTypeValue = Number(match.value);
    }
  }

  if (
    bankTypeValue == null &&
    row?.type !== undefined &&
    row?.type !== null &&
    row?.type !== ""
  ) {
    const numericValue = Number(row.type);
    if (!Number.isNaN(numericValue) && numericValue > 0) {
      bankTypeValue = numericValue;
    } else if (typeof row.type === "string" && row.type.trim() !== "") {
      const t = row.type.trim();
      if (t === "kasar") {
        const matchingBank = types.find(
          (bank) =>
            String(bank.label).toLowerCase() === "kasar" ||
            String(bank.label).toLowerCase().includes("kasar"),
        );
        if (matchingBank) {
          bankTypeValue = Number(matchingBank.value);
        }
      } else if (!t.startsWith("__acctname__:")) {
        bankTypeValue = row.type;
      }
    }
  }

  let typeNum = num(bankTypeValue, NaN);
  if (Number.isFinite(typeNum) && typeNum > 0) {
    return typeNum;
  }

  // Fixed ledgers (cash / wallet) may have no Type selected in UI —
  // resolve bank_account_id from pay_mode + paymentTypes labels.
  // Kasar is intentionally bankless — never resolve an account for it.
  const mode = String(row?.pay_mode ?? "")
    .toLowerCase()
    .trim();
  if (isKasarPayMode(mode)) {
    return 0;
  }
  if (["cash", "wallet"].includes(mode) && types.length > 0) {
    const keyword = mode.replace("_", " ");
    const matchingBank = types.find((bank) => {
      const label = String(bank.label || "")
        .toLowerCase()
        .trim();
      return label === keyword || label.includes(keyword);
    });
    if (matchingBank) {
      typeNum = num(matchingBank.value, NaN);
      if (Number.isFinite(typeNum) && typeNum > 0) {
        return typeNum;
      }
    }
  }

  return 0;
}

export function resolvePaymentModeLabel(
  row,
  payModeLabels = PAYMENT_METHOD_LABELS,
) {
  const urdMode = urdApiModeFromPayMode(row?.pay_mode ?? row?.payment_mode);
  if (urdMode) return urdMode;

  const key = String(row?.pay_mode ?? row?.payment_mode ?? "").trim();
  if (!key) return "";
  return payModeLabels[key] || key;
}

export function resolvePaymentBankName(
  bankAccountId,
  row,
  paymentTypes = [],
  payModeLabels = PAYMENT_METHOD_LABELS,
) {
  if (isUrdLinkedPayMode(row?.pay_mode) || isKasarPayMode(row?.pay_mode)) {
    return "";
  }

  if (row?.bankDisplayName && String(row.bankDisplayName).trim()) {
    return String(row.bankDisplayName).trim();
  }

  if (bankAccountId > 0 && Array.isArray(paymentTypes) && paymentTypes.length) {
    const match = paymentTypes.find(
      (b) => Number(b.value ?? b.id) === Number(bankAccountId),
    );
    if (match) {
      const label = match.label ?? match.bankName ?? match.bank_name ?? "";
      if (String(label).trim()) return String(label).trim();
    }
  }

  const modeLabel = resolvePaymentModeLabel(row, payModeLabels);
  return modeLabel || "";
}

function resolvePaymentLineId(row, isUpdate) {
  if (!isUpdate) return undefined;

  const raw = row?.paymentId ?? row?.id ?? row?.payment_id;
  if (row?.isExistingPayment === true) {
    if (raw != null && raw !== "" && raw !== "new") {
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : null;
    }
    return null;
  }

  if (hasExistingPaymentLineById(raw)) {
    return Number(raw);
  }

  return null;
}

function shouldIncludePaymentRow(row, options = {}) {
  const payMode = String(row?.pay_mode ?? row?.payment_mode ?? "").trim();
  if (!payMode) return false;

  const amount = parsePaymentLineAmount(row);
  if (amount <= 0) return false;

  if (isUrdLinkedPayMode(payMode)) {
    const urdId = Number(row?.urd_document_id);
    const hasUrdData = Boolean(row?.urd_data || row?.old_purchase_data);
    if (!hasUrdData && (!Number.isFinite(urdId) || urdId <= 0)) return false;
  }

  if (options.excludeAdvance) {
    const tnx = String(row?.tnx_type ?? "").toUpperCase();
    if (tnx === "ADVANCE") return false;
  }

  return true;
}

function buildUrdLinkedPaymentLine(row, options = {}) {
  const { isUpdate = false, createKey, persistKeys } = options;
  const amount = parsePaymentLineAmount(row);
  const payment_mode = resolvePaymentModeLabel(row);
  const urdId = Number(row.urd_document_id);
  const remarks = String(row?.remarks ?? "").trim();

  const line = {
    payment_mode,
    amount,
    remarks,
  };
  if (Number.isFinite(urdId) && urdId > 0) {
    line.urd_document_id = urdId;
  }

  if (isUpdate) {
    line.id = resolvePaymentLineId(row, true);
  }

  const hasExisting =
    line.id != null && Number.isFinite(Number(line.id)) && Number(line.id) > 0;

  if (createKey) {
    const { idempotency_key, generatedKey } = resolvePaymentLineIdempotencyKey(
      row,
      hasExisting,
      createKey,
    );
    if (idempotency_key) {
      line.idempotency_key = idempotency_key;
    }
    if (generatedKey && Array.isArray(persistKeys)) {
      persistKeys.push({
        rowId: row.id,
        rowKey: row.rowKey,
        paymentId: row.paymentId,
        key: generatedKey,
      });
    }
  }

  return line;
}

function buildKasarPaymentLine(row, options = {}) {
  const {
    isUpdate = false,
    payModeLabels = PAYMENT_METHOD_LABELS,
    createKey,
    persistKeys,
  } = options;
  const amount = parsePaymentLineAmount(row);
  const payment_mode = resolvePaymentModeLabel(row, payModeLabels);
  const remarks = String(row?.remarks ?? "").trim();

  const line = {
    payment_mode,
    amount,
    remarks,
  };

  if (isUpdate) {
    line.id = resolvePaymentLineId(row, true);
  }

  const hasExisting =
    line.id != null && Number.isFinite(Number(line.id)) && Number(line.id) > 0;

  if (createKey) {
    const { idempotency_key, generatedKey } = resolvePaymentLineIdempotencyKey(
      row,
      hasExisting,
      createKey,
    );
    if (idempotency_key) {
      line.idempotency_key = idempotency_key;
    }
    if (generatedKey && Array.isArray(persistKeys)) {
      persistKeys.push({
        rowId: row.id,
        rowKey: row.rowKey,
        paymentId: row.paymentId,
        key: generatedKey,
      });
    }
  }

  return line;
}

function buildBankPaymentLine(row, options = {}) {
  const {
    isUpdate = false,
    paymentTypes = [],
    payModeLabels = PAYMENT_METHOD_LABELS,
    createKey,
    persistKeys,
  } = options;

  const bank_account_id = resolvePaymentBankAccountId(row, paymentTypes);
  const amount = parsePaymentLineAmount(row);
  const payment_mode = resolvePaymentModeLabel(row, payModeLabels);
  const bank_name = resolvePaymentBankName(
    bank_account_id,
    row,
    paymentTypes,
    payModeLabels,
  );
  const remarks = String(row?.remarks ?? "").trim();

  const line = {
    bank_account_id,
    amount,
    payment_mode,
    bank_name,
    remarks,
  };

  if (isUpdate) {
    line.id = resolvePaymentLineId(row, true);
  }

  const hasExisting =
    line.id != null && Number.isFinite(Number(line.id)) && Number(line.id) > 0;

  if (createKey) {
    const { idempotency_key, generatedKey } = resolvePaymentLineIdempotencyKey(
      row,
      hasExisting,
      createKey,
    );
    if (idempotency_key) {
      line.idempotency_key = idempotency_key;
    }
    if (generatedKey && Array.isArray(persistKeys)) {
      persistKeys.push({
        rowId: row.id,
        rowKey: row.rowKey,
        paymentId: row.paymentId,
        key: generatedKey,
      });
    }
  }

  return line;
}

/**
 * Build standard `payments[]` for create or update payloads.
 */
export function buildStandardPaymentLines(rows, options = {}) {
  const {
    isUpdate = false,
    paymentTypes = [],
    payModeLabels = PAYMENT_METHOD_LABELS,
    createKey,
    persistKeys,
    excludeAdvance = false,
    requireBankAccount = true,
  } = options;

  if (!Array.isArray(rows)) return [];

  const lineOptions = {
    isUpdate,
    paymentTypes,
    payModeLabels,
    createKey,
    persistKeys,
  };

  return rows
    .filter((row) => shouldIncludePaymentRow(row, { excludeAdvance }))
    .map((row) => {
      if (isUrdLinkedPayMode(row.pay_mode)) {
        return buildUrdLinkedPaymentLine(row, lineOptions);
      }
      if (isKasarPayMode(row.pay_mode)) {
        return buildKasarPaymentLine(row, lineOptions);
      }
      return buildBankPaymentLine(row, lineOptions);
    })
    .filter((p) => {
      if (p.amount <= 0) return false;
      if (isUrdLinkedPaymentMode(p.payment_mode)) {
        return true;
      }
      if (isKasarPaymentMode(p.payment_mode)) {
        return true;
      }
      if (requireBankAccount && !(p.bank_account_id > 0)) return false;
      return true;
    });
}

/** Stable fingerprint for change detection (ignores idempotency_key). */
export function fingerprintStandardPaymentLine(line = {}) {
  const idRaw = line.id ?? line.payment_id;
  const id =
    idRaw != null && idRaw !== "" && hasExistingPaymentLineById(idRaw)
      ? Number(idRaw)
      : "new";
  if (isUrdLinkedPaymentMode(line.payment_mode)) {
    return [
      id,
      String(line.payment_mode ?? ""),
      Number(line.urd_document_id) || 0,
      String(line.remarks ?? "").trim(),
      Number(line.amount) || 0,
    ].join("|");
  }
  if (isKasarPaymentMode(line.payment_mode)) {
    return [
      id,
      String(line.payment_mode ?? ""),
      String(line.remarks ?? "").trim(),
      Number(line.amount) || 0,
    ].join("|");
  }
  return [
    id,
    Number(line.bank_account_id) || 0,
    String(line.payment_mode ?? line.pay_mode ?? ""),
    String(line.bank_name ?? "").trim(),
    String(line.remarks ?? "").trim(),
    Number(line.amount) || 0,
  ].join("|");
}
